import { useLayoutEffect } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5stock from "@amcharts/amcharts5/stock";
import am5themes_Responsive from "@amcharts/amcharts5/themes/Responsive";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { STOCK_DATA, COMPARE_DATA } from "../../../../data/overviewChart";

export default function StockChart() {

  useLayoutEffect(() => {
    const root = am5.Root.new("stockChart");

    root.setThemes([
      am5themes_Animated.new(root),
      am5themes_Responsive.new(root)
    ]);

    // Create a stock chart
    const stockChart = root.container.children.push(am5stock.StockChart.new(root, {
      stockPositiveColor: am5.color("#22CCEE"),
      stockNegativeColor: am5.color("#444CE7"),
      volumePositiveColor: am5.color("#22CCEE"),
      volumeNegativeColor: am5.color("#444CE7")
    }));

    // Set global number format
    root.numberFormatter.set("numberFormat", "#,###.00");

    // Create a main stock panel (chart)
    const mainPanel = stockChart.panels.push(am5stock.StockPanel.new(root, {
      wheelY: "none",
      panX: true,
      panY: true
    }));

    const valueAxis = mainPanel.yAxes.push(am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererY.new(root, {
        pan: "zoom"
      }),
      extraMin: 0.1, // adds some space for for main series
      tooltip: am5.Tooltip.new(root, {}),
      numberFormat: "#,###.00",
      extraTooltipPrecision: 2
    }));

    const dateAxis = mainPanel.xAxes.push(am5xy.GaplessDateAxis.new(root, {
      baseInterval: {
        timeUnit: "day",
        count: 1
      },
      renderer: am5xy.AxisRendererX.new(root, {}),
      tooltip: am5.Tooltip.new(root, {})
    }));

    dateAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#BDBDC5")
    });

    // Add series
    const valueSeries = mainPanel.series.push(am5xy.CandlestickSeries.new(root, {
      name: "MSFT",
      clustered: false,
      valueXField: "Date",
      valueYField: "Close",
      highValueYField: "High",
      lowValueYField: "Low",
      openValueYField: "Open",
      calculateAggregates: true,
      xAxis: dateAxis,
      yAxis: valueAxis,
      legendValueText: "open: [bold]{openValueY}[/] high: [bold]{highValueY}[/] low: [bold]{lowValueY}[/] close: [bold]{valueY}[/]",
      legendRangeValueText: ""
    }));

    stockChart.set("stockSeries", valueSeries);

    const volumeAxisRenderer = am5xy.AxisRendererY.new(root, {
      inside: true
    });

    volumeAxisRenderer.labels.template.set("forceHidden", true);
    volumeAxisRenderer.grid.template.set("forceHidden", true);

    const volumeValueAxis = mainPanel.yAxes.push(am5xy.ValueAxis.new(root, {
      numberFormat: "#.#a",
      height: am5.percent(20),
      y: am5.percent(100),
      centerY: am5.percent(100),
      renderer: volumeAxisRenderer
    }));

    // Add series
    const volumeSeries = mainPanel.series.push(am5xy.ColumnSeries.new(root, {
      name: "Volume",
      clustered: false,
      valueXField: "Date",
      valueYField: "Volume",
      xAxis: dateAxis,
      yAxis: volumeValueAxis,
      legendValueText: "[bold]{valueY.formatNumber('#,###.0a')}[/]"
    }));

    volumeSeries.columns.template.setAll({
      strokeOpacity: 0,
      fillOpacity: 0.5
    });

    // color columns by stock rules
    volumeSeries.columns.template.adapters.add("fill", (fill, target) => {
      const dataItem = target.dataItem;
      if (dataItem) {
        return stockChart.getVolumeColor(dataItem, am5.color('#747480'), am5.color('#747480'));
      }
      return fill;
    });

    stockChart.set("volumeSeries", volumeSeries);

    valueSeries.data.setAll(STOCK_DATA);
    volumeSeries.data.setAll(STOCK_DATA);

    const loadData = (series: [am5xy.XYSeries]) => {

      const data = COMPARE_DATA;

      // Process data (convert dates and values)
      var processor = am5.DataProcessor.new(root, {
        dateFields: ["Date"],
        dateFormat: "yyyy-MM-dd",
        numericFields: ["Open", "High", "Low", "Close", "Volume"]
      });
      processor.processMany(data);

      // Set data
      am5.array.each(series, (item) => {
        item?.data.setAll(data);
      });
    }

    const addComparingSeries = (label: string) => {
      const series = am5xy.SmoothedXLineSeries.new(root, {
        name: label,
        valueYField: label === "first" ? "Close" : "Low",
        calculateAggregates: true,
        valueXField: "Date",
        xAxis: dateAxis,
        yAxis: valueAxis,
        stroke: label === "first" ? am5.color("#B0BEC5") : am5.color("#3A3A4A"),
        legendValueText: "{valueY.formatNumber('#.00')}"
      });
      if (label === "first") {
        series.strokes.template.setAll({
          strokeWidth: 3,
          strokeDasharray: [0, 0]
        });
      } else {
        series.strokes.template.setAll({
          strokeWidth: 3,
          strokeDasharray: [10, 5]
        });
      }
      const comparingSeries = stockChart.addComparingSeries(series);
      loadData([comparingSeries]);
    };

    // Add comparing series
    addComparingSeries("first");
    addComparingSeries("second");

    root._logo?.dispose();

    return () => {
      root.dispose();
    }
  }, []);

  return (
    <div id="stockChart" style={{ width: "100%", height: "800px" }}>
    </div>
  );
}
